/**
 * AUTOMATIC UPLOAD ERROR TRACKING
 * 
 * This utility automatically logs upload failures to the database
 * so we can diagnose issues without requiring manual user reports.
 */

import { supabase } from '@/integrations/supabase/client';

export type UploadErrorType = 'validation' | 'upload' | 'network' | 'unknown';

interface UploadErrorContext {
  errorType: UploadErrorType;
  errorMessage: string;
  errorCode?: string;
  file?: File;
  error?: Error;
  attemptNumber?: number;
  bucket?: string;
  hadImage?: boolean;
  contentLength?: number;
}

/**
 * Get sanitized browser information (no PII)
 */
function getBrowserInfo() {
  return {
    userAgent: navigator.userAgent,
    vendor: navigator.vendor,
    platform: navigator.platform,
    language: navigator.language,
    onLine: navigator.onLine,
    cookiesEnabled: navigator.cookieEnabled,
    memory: (performance as any).memory ? {
      usedJSHeapSize: Math.round((performance as any).memory.usedJSHeapSize / 1024 / 1024),
      totalJSHeapSize: Math.round((performance as any).memory.totalJSHeapSize / 1024 / 1024),
      limit: Math.round((performance as any).memory.jsHeapSizeLimit / 1024 / 1024),
    } : null,
  };
}

/**
 * Get sanitized file information (no file content or sensitive data)
 */
function getFileInfo(file?: File) {
  if (!file) return null;
  
  return {
    size: file.size,
    type: file.type,
    // Sanitize filename - keep extension only
    extension: file.name.split('.').pop()?.toLowerCase() || 'unknown',
    lastModified: file.lastModified,
  };
}

/**
 * Automatically track upload errors to database
 * This runs silently in the background - doesn't block the UI
 */
export async function trackUploadError(context: UploadErrorContext): Promise<void> {
  try {
    // Get current user
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      console.warn('[Upload Error Tracker] Cannot track error - user not authenticated');
      return;
    }

    const errorData = {
      user_id: user.id,
      error_type: context.errorType,
      error_message: context.errorMessage,
      error_code: context.errorCode || null,
      file_info: getFileInfo(context.file),
      browser_info: getBrowserInfo(),
      upload_context: {
        attemptNumber: context.attemptNumber || 1,
        bucket: context.bucket || 'bulletin-images',
        hadImage: context.hadImage ?? false,
        contentLength: context.contentLength || 0,
        timestamp: new Date().toISOString(),
      },
      stack_trace: context.error?.stack || null,
    };

    // Insert error log (don't await - fire and forget)
    supabase
      .from('upload_errors')
      .insert([errorData])
      .then(({ error }) => {
        if (error) {
          console.error('[Upload Error Tracker] Failed to log error:', error);
        } else {
          console.log('[Upload Error Tracker] Error logged successfully:', {
            type: context.errorType,
            message: context.errorMessage.substring(0, 50),
          });
        }
      });

  } catch (trackingError) {
    // Never let error tracking break the app
    console.error('[Upload Error Tracker] Tracking failed:', trackingError);
  }
}

/**
 * Track validation errors (file type, size, format issues)
 */
export function trackValidationError(message: string, file?: File, error?: Error) {
  trackUploadError({
    errorType: 'validation',
    errorMessage: message,
    file,
    error,
  });
}

/**
 * Track upload errors (storage, network, backend issues)
 */
export function trackUploadFailure(message: string, file?: File, error?: Error, context?: {
  attemptNumber?: number;
  bucket?: string;
}) {
  trackUploadError({
    errorType: 'upload',
    errorMessage: message,
    errorCode: (error as any)?.code || (error as any)?.status,
    file,
    error,
    attemptNumber: context?.attemptNumber,
    bucket: context?.bucket,
  });
}

/**
 * Track network errors (timeout, connection issues)
 */
export function trackNetworkError(message: string, file?: File, error?: Error) {
  trackUploadError({
    errorType: 'network',
    errorMessage: message,
    file,
    error,
  });
}

/**
 * Detect if a post attempt had media but no media URL
 * This catches silent failures where upload completes but URL is missing
 */
export function detectSilentUploadFailure(
  hadImage: boolean,
  mediaUrl: string | null,
  content: string
) {
  if (hadImage && !mediaUrl) {
    trackUploadError({
      errorType: 'upload',
      errorMessage: 'Silent upload failure: Image selected but no media URL after upload',
      hadImage: true,
      contentLength: content?.length || 0,
    });
  }
}
